import React from "react";
import { useParams, Link } from "react-router-dom";
import DashboardLayout from "../../components/DashboardLayout";
import { SERVICES, PACKAGES } from "../../data/staticData";
import { SERVICE_EXTRAS } from "../../data/serviceExtras";
import { turnaroundMaxDays } from "../../lib/pricing";
import { Check, X, Calendar, ArrowUpRight, ChevronLeft } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../../components/ui/accordion";

export default function DashboardServiceDetail() {
  const { slug } = useParams();
  const service = SERVICES.find((s) => s.slug === slug);
  const extras = SERVICE_EXTRAS[slug];
  const packages = PACKAGES[slug] || [];

  if (!service) {
    return (
      <DashboardLayout>
        <div className="text-sm text-inkSoft">Service not found. <Link to="/dashboard/services" className="text-terra">Back to catalog</Link></div>
      </DashboardLayout>
    );
  }

  const Icon = service.icon;
  const maxTurnaround = packages.length ? Math.max(...packages.map((p) => turnaroundMaxDays(p.turnaround))) : 7;

  // Build a feature matrix for compact comparison-style cards
  const allFeatures = Array.from(new Set(packages.flatMap((p) => p.features)));

  return (
    <DashboardLayout>
      <Link to="/dashboard/services" className="inline-flex items-center gap-1.5 text-sm text-inkSoft hover:text-ink mb-4"><ChevronLeft size={14} /> Catalog</Link>

      {/* HERO banner (compact, dark, fits inside dashboard chrome) */}
      <section className="rounded-3xl bg-ink text-white relative overflow-hidden grain" data-testid="dashboard-service-hero">
        <div className="absolute -right-20 -top-20 h-72 w-72 rounded-full bg-terra/25 blur-3xl" />
        <div className="absolute -left-10 -bottom-20 h-60 w-60 rounded-full bg-white/5 blur-3xl" />
        <div className="relative p-8 sm:p-12 grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 bg-white/5 border border-white/10 rounded-full px-3 py-1.5 text-[11px] font-mono tracking-widest uppercase">
              <Icon size={12} /> {service.title}
            </div>
            <h1 className="mt-5 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.05]" data-testid="dashboard-service-title">
              {extras?.tagline || service.title}
            </h1>
            <div className="mt-5 inline-flex items-center gap-2 text-white/80 text-sm">
              <Calendar size={14} /> Delivery: up to {maxTurnaround} days
            </div>
          </div>
          <div className="lg:col-span-5 hidden lg:block">
            <ul className="space-y-2.5 max-w-md ml-auto">
              {(extras?.hero_bullets || service.benefits).slice(0, 3).map((b) => (
                <li key={b} className="flex items-start gap-2 text-white/85 text-sm">
                  <span className="mt-0.5 h-5 w-5 rounded-full bg-terra grid place-items-center shrink-0"><Check size={12} strokeWidth={3} /></span>
                  {b}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* PRICING TIER CARDS */}
      <section className="mt-8" data-testid="dashboard-service-pricing">
        <div className={`grid grid-cols-1 ${packages.length >= 3 ? "md:grid-cols-3" : "md:grid-cols-2"} gap-4`}>
          {packages.map((p) => {
            return (
              <div
                key={p.id}
                className={`rounded-2xl overflow-hidden border ${p.highlight ? "border-ink ring-2 ring-terra/40" : "border-line"} bg-white flex flex-col`}
                data-testid={`dashboard-pricing-${p.id}`}
              >
                {/* Colored top */}
                <div className={`px-6 pt-6 pb-8 ${p.highlight ? "bg-ink text-white" : "bg-surfaceAlt text-ink"}`}>
                  {p.highlight && <div className="inline-flex items-center bg-terra text-white text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full mb-3">Most popular</div>}
                  <div className="font-display text-2xl font-bold tracking-tight">{p.name}</div>
                  <div className="mt-3 flex items-baseline gap-1">
                    <span className="font-mono text-4xl font-semibold tracking-tighter">${p.price}</span>
                  </div>
                  <div className={`mt-1 text-xs ${p.highlight ? "text-white/70" : "text-inkSoft"}`}>Turnaround · {p.turnaround}</div>
                </div>
                {/* Feature matrix */}
                <ul className="p-6 space-y-2.5 text-sm flex-1">
                  {allFeatures.map((f) => {
                    const included = p.features.includes(f);
                    return (
                      <li key={f} className={`flex items-start gap-2 ${included ? "text-ink/85" : "text-inkSoft/70"}`}>
                        {included ? (
                          <Check size={14} className="text-terra mt-0.5 shrink-0" />
                        ) : (
                          <X size={14} className="text-inkSoft/50 mt-0.5 shrink-0" />
                        )}
                        <span className={included ? "" : "line-through"}>{f}</span>
                      </li>
                    );
                  })}
                </ul>
                <div className="p-6 pt-0">
                  <Link
                    to={`/dashboard/order/${slug}/${p.id}`}
                    className={`inline-flex items-center justify-center gap-2 w-full px-5 py-3 rounded-xl text-sm font-semibold transition ${
                      p.highlight ? "bg-terra text-white hover:bg-terra-hover" : "bg-ink text-white hover:bg-black"
                    }`}
                    data-testid={`dashboard-order-now-${p.id}`}
                  >
                    Order now <ArrowUpRight size={14} />
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* HOW IT WORKS */}
      {extras?.process && (
        <section className="mt-12" data-testid="dashboard-service-process">
          <div className="text-center mb-8">
            <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">How Our {service.title} Works</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {extras.process.map((step, i) => (
              <div key={step.title} className="card-soft p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="h-8 w-8 rounded-full bg-ink text-white grid place-items-center font-mono text-sm font-semibold">{i + 1}</div>
                </div>
                <h3 className="font-display text-lg font-semibold tracking-tight">{step.title}</h3>
                <p className="mt-3 text-sm text-inkSoft leading-relaxed">{step.body}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* BENEFITS */}
      {extras?.benefits && (
        <section className="mt-12" data-testid="dashboard-service-benefits">
          <div className="text-center mb-8">
            <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">{service.title} Benefits</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {extras.benefits.map((b) => {
              const BIcon = b.icon;
              return (
                <div key={b.title} className="card-soft p-6">
                  <div className="h-10 w-10 rounded-lg bg-terra/10 grid place-items-center text-terra"><BIcon size={18} /></div>
                  <h3 className="font-display text-base font-semibold tracking-tight mt-4">{b.title}</h3>
                  <p className="mt-2 text-sm text-inkSoft leading-relaxed">{b.body}</p>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* FAQ */}
      {extras?.faqs && (
        <section className="mt-12 mb-8" data-testid="dashboard-service-faqs">
          <div className="rounded-3xl bg-ink text-white px-6 sm:px-10 py-5">
            <h2 className="font-display text-xl font-semibold text-center">Got questions? We've got answers</h2>
          </div>
          <div className="bg-white border border-line border-t-0 rounded-b-3xl overflow-hidden -mt-1">
            <Accordion type="single" collapsible>
              {extras.faqs.slice(0, 6).map((f, i) => (
                <AccordionItem key={i} value={`f-${i}`} className="border-b border-line last:border-0">
                  <AccordionTrigger className="px-6 py-4 text-left font-medium hover:no-underline" data-testid={`dashboard-faq-${i}`}>{f.q}</AccordionTrigger>
                  <AccordionContent className="px-6 pb-4 text-inkSoft text-sm">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>
      )}
    </DashboardLayout>
  );
}
